// Globals
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ValidateErrorEntity } from 'rc-field-form/lib/interface';

// Components
import { StyledForm, StyledPageWrapper, StyledBlock, StyledFieldsWrap, StyledButton } from './CreateBannerFormStyles';
import { FormItem, IFormInstance, UploadDragger } from '@/components';
import { DatePicker, Select } from '@/ui';
import BannersPreview from '../BannersPreview/BannersPreview';

// Modules
import { LOCALES_FIELD_NAME, LocalesFormFields } from '@/modules/Locales';

// Models
import { IBanner } from '@/models/banners.model';

// Helpers
import { IMAGE_MIME_TYPES } from '@/constants/files';
import { useBannerFormValidationRules } from '../../hooks';
import { getActiveOptions, BannersFields, TBannerFormValues, normalizeBannerValues } from '../../helpers';

type TCreateBannerForm = {
  formInstance: IFormInstance;
  initialValues: IBanner | null;
  onSubmit: (values: TBannerFormValues) => Promise<void>;
  isEdit: boolean;
  isOpen: boolean;
  isLoading: boolean;
  onFinishFailed: (error: ValidateErrorEntity) => void;
};

const CreateBannerForm = ({
  formInstance,
  initialValues,
  onSubmit,
  isEdit,
  isOpen,
  isLoading,
  onFinishFailed,
}: TCreateBannerForm) => {
  const { t } = useTranslation(['banners']);
  const rules = useBannerFormValidationRules();

  useEffect(() => {
    formInstance.setFieldsValue(normalizeBannerValues(initialValues as IBanner, isEdit));
  }, [isEdit, isOpen, initialValues, formInstance]);

  return (
    <StyledPageWrapper>
      <StyledForm onFinish={onSubmit} form={formInstance} onFinishFailed={onFinishFailed}>
        <StyledBlock>
          <FormItem style={{ width: '100%' }} name={LOCALES_FIELD_NAME}>
            <LocalesFormFields
              labelPrefix={'banners_form_'}
              ns={'banners'}
              textAreaFields={['description']}
              withoutRulesFields={['title', 'description', 'link']}
              rules={rules[BannersFields.WEB_MEDIA]}
            />
          </FormItem>

          <StyledFieldsWrap>
            <FormItem name={BannersFields.ACTIVE} label={t('banners_list_status')} rules={rules[BannersFields.ACTIVE]}>
              <Select options={getActiveOptions(t)} />
            </FormItem>
            <FormItem
              name={BannersFields.START_DATE}
              label={t('banners_list_start')}
              rules={rules[BannersFields.START_DATE]}
            >
              <DatePicker />
            </FormItem>
            <FormItem name={BannersFields.END_DATE} label={t('banners_list_end')} rules={rules[BannersFields.END_DATE]}>
              <DatePicker />
            </FormItem>
          </StyledFieldsWrap>
        </StyledBlock>

        <StyledBlock>
          <FormItem
            name={BannersFields.WEB_MEDIA}
            label={t('banners_photo_size', { width: 1750, height: 250 })}
            rules={rules[BannersFields.WEB_MEDIA]}
            style={{ width: '100%' }}
          >
            <UploadDragger acceptance={IMAGE_MIME_TYPES} />
          </FormItem>
          <FormItem
            name={BannersFields.MOBILE_MEDIA}
            label={t('banners_photo_size', { width: 375, height: 250 })}
            rules={rules[BannersFields.MOBILE_MEDIA]}
            style={{ width: '100%' }}
          >
            <UploadDragger acceptance={IMAGE_MIME_TYPES} />
          </FormItem>
        </StyledBlock>

        <StyledButton type="primary" htmlType="submit" disabled={isLoading} loading={isLoading}>
          {isEdit ? t('update_banner') : t('add_banner')}
        </StyledButton>
        <div id="preview">
          <BannersPreview formInstance={formInstance} />
        </div>
      </StyledForm>
    </StyledPageWrapper>
  );
};

export default CreateBannerForm;
